import React, { useState } from "react";
import { Button } from "@/components/ui/buttons/button";
import { useAuth } from "@/providers/auth-provider";

type Step = "login" | "signup" | "confirm" | "forgot" | "reset";

const inputClass =
  "w-full bg-white/10 border-2 border-white/10 rounded-xl py-3 px-4 text-white text-base font-bold text-center focus:outline-none focus:border-pk-blue-3 transition-all placeholder:text-white/20";

export const LoginScreen: React.FC = () => {
  const {
    loginWithEmail,
    signupWithEmail,
    confirmEmailSignup,
    forgotPassword,
    confirmForgotPassword,
  } = useAuth();

  const [step, setStep] = useState<Step>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await loginWithEmail(email, password);
    setLoading(false);
    if (!result.ok) {
      setError(result.message || "Login failed");
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await signupWithEmail(email, password);
    setLoading(false);
    if (!result.ok) {
      setError(result.message || "Signup failed");
      return;
    }
    setInfo("We sent a confirmation code to your email.");
    setStep("confirm");
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await confirmEmailSignup(email, code);
    setLoading(false);
    if (!result.ok) {
      setError(result.message || "Confirmation failed");
      return;
    }
    setInfo("Account confirmed! You can now log in.");
    setCode("");
    setStep("login");
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await forgotPassword(email);
    setLoading(false);
    if (!result.ok) {
      setError(result.message || "Failed to send reset code");
      return;
    }
    setInfo("We sent a password reset code to your email.");
    setStep("reset");
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await confirmForgotPassword(email, code, newPassword);
    setLoading(false);
    if (!result.ok) {
      setError(result.message || "Failed to reset password");
      return;
    }
    setInfo("Password reset! You can now log in.");
    setCode("");
    setNewPassword("");
    setStep("login");
  };

  return (
    <main
      className="mx-auto my-auto fixed inset-0 z-[100] flex flex-col items-center justify-center bg-pk-night-1"
      style={{
        height: "min(100svh, calc(480px * 18 / 9))",
        maxWidth: "480px",
      }}
    >
      <div className="relative z-10 flex flex-col items-center gap-6 w-full max-w-xs px-6 text-center">
        <div className="relative w-[220px] xs:w-[260px] h-auto mb-2">
          <div className="absolute inset-0 bg-pk-purple-3 blur-2xl opacity-20 rounded-full" />
          <img
            src="/assets/app-ui/logo.png"
            alt="Logo"
            className="relative z-10 w-full h-full object-contain filter drop-shadow-[0_0_15px_rgba(122,122,122,0.25)]"
          />
        </div>

        {step === "login" && (
          <form
            onSubmit={handleLogin}
            className="w-full flex flex-col gap-3"
          >
            <h2 className="text-xl font-black text-white tracking-tight uppercase italic drop-shadow-md">
              Log In
            </h2>
            <input
              type="email"
              placeholder="Email"
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
            <input
              type="password"
              placeholder="Password"
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button
              variant="purple"
              type="submit"
              loading={loading}
              className="w-full"
            >
              <span className="text-white text-lg font-black tracking-wider uppercase italic drop-shadow-sm px-6 py-1">
                Log In
              </span>
            </Button>
            <button
              type="button"
              className="text-pk-gray-3 text-xs font-bold underline"
              onClick={() => {
                setError(null);
                setInfo(null);
                setStep("signup");
              }}
            >
              Need an account? Sign up
            </button>
            <button
              type="button"
              className="text-pk-gray-3 text-xs font-bold underline"
              onClick={() => {
                setError(null);
                setInfo(null);
                setStep("forgot");
              }}
            >
              Forgot password?
            </button>
          </form>
        )}

        {step === "signup" && (
          <form
            onSubmit={handleSignup}
            className="w-full flex flex-col gap-3"
          >
            <h2 className="text-xl font-black text-white tracking-tight uppercase italic drop-shadow-md">
              Sign Up
            </h2>
            <input
              type="email"
              placeholder="Email"
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
            <input
              type="password"
              placeholder="Password"
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button
              variant="purple"
              type="submit"
              loading={loading}
              className="w-full"
            >
              <span className="text-white text-lg font-black tracking-wider uppercase italic drop-shadow-sm px-6 py-1">
                Sign Up
              </span>
            </Button>
            <button
              type="button"
              className="text-pk-gray-3 text-xs font-bold underline"
              onClick={() => {
                setError(null);
                setInfo(null);
                setStep("login");
              }}
            >
              Already have an account? Log in
            </button>
          </form>
        )}

        {step === "confirm" && (
          <form
            onSubmit={handleConfirm}
            className="w-full flex flex-col gap-3"
          >
            <h2 className="text-xl font-black text-white tracking-tight uppercase italic drop-shadow-md">
              Confirm Email
            </h2>
            <p className="text-pk-gray-3 text-xs font-bold leading-relaxed">
              Enter the code sent to {email}
            </p>
            <input
              type="text"
              placeholder="Confirmation code"
              className={inputClass}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              required
            />
            <Button
              variant="purple"
              type="submit"
              loading={loading}
              className="w-full"
            >
              <span className="text-white text-lg font-black tracking-wider uppercase italic drop-shadow-sm px-6 py-1">
                Confirm
              </span>
            </Button>
          </form>
        )}

        {step === "forgot" && (
          <form
            onSubmit={handleForgotPassword}
            className="w-full flex flex-col gap-3"
          >
            <h2 className="text-xl font-black text-white tracking-tight uppercase italic drop-shadow-md">
              Reset Password
            </h2>
            <p className="text-pk-gray-3 text-xs font-bold leading-relaxed">
              Enter your email and we'll send you a reset code.
            </p>
            <input
              type="email"
              placeholder="Email"
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
            <Button
              variant="purple"
              type="submit"
              loading={loading}
              className="w-full"
            >
              <span className="text-white text-lg font-black tracking-wider uppercase italic drop-shadow-sm px-6 py-1">
                Send Code
              </span>
            </Button>
            <button
              type="button"
              className="text-pk-gray-3 text-xs font-bold underline"
              onClick={() => {
                setError(null);
                setInfo(null);
                setStep("login");
              }}
            >
              Back to log in
            </button>
          </form>
        )}

        {step === "reset" && (
          <form
            onSubmit={handleResetPassword}
            className="w-full flex flex-col gap-3"
          >
            <h2 className="text-xl font-black text-white tracking-tight uppercase italic drop-shadow-md">
              Set New Password
            </h2>
            <p className="text-pk-gray-3 text-xs font-bold leading-relaxed">
              Enter the code sent to {email} and a new password.
            </p>
            <input
              type="text"
              placeholder="Reset code"
              className={inputClass}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              required
            />
            <input
              type="password"
              placeholder="New password"
              className={inputClass}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <Button
              variant="purple"
              type="submit"
              loading={loading}
              className="w-full"
            >
              <span className="text-white text-lg font-black tracking-wider uppercase italic drop-shadow-sm px-6 py-1">
                Reset Password
              </span>
            </Button>
            <button
              type="button"
              className="text-pk-gray-3 text-xs font-bold underline"
              onClick={() => {
                setError(null);
                setInfo(null);
                setStep("login");
              }}
            >
              Back to log in
            </button>
          </form>
        )}

        {error && (
          <p className="text-red-400 text-xs font-bold uppercase tracking-wider">
            {error}
          </p>
        )}
        {!error && info && (
          <p className="text-green-400 text-xs font-bold uppercase tracking-wider">
            {info}
          </p>
        )}

      </div>
    </main>
  );
};
